import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSiteContent } from '@/context/SiteContentContext';
import { LocalBusinessJsonLd, FAQJsonLd, WebSiteJsonLd, OrganizationJsonLd } from './JsonLd';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogTitle?: string;
  ogDescription?: string;
  noIndex?: boolean;
  includeFAQ?: boolean;
  pageType?: 'website' | 'article' | 'service';
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonical,
  ogImage,
  ogTitle,
  ogDescription,
  noIndex = false,
  includeFAQ = false,
  pageType = 'website',
}) => {
  const content = useSiteContent();
  
  const { data: siteVerificationId } = useQuery({
    queryKey: ['google-site-verification'],
    queryFn: async () => {
      const { data } = await supabase
        .from('business_settings')
        .select('google_site_verification_id')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return (data as any)?.google_site_verification_id as string | null;
    },
    staleTime: 1000 * 60 * 30,
  });
  const { template, seoTitle, seoDescription, seoCanonical, faq } = content;
  
  const brand = template.brand;
  const finalTitle = title || seoTitle || brand?.name || 'Home';
  const finalDescription = description || seoDescription || '';
  const finalCanonical = canonical || seoCanonical || '';
  const finalOgImage = ogImage || content.heroImageUrl || '';
  const finalOgTitle = ogTitle || finalTitle;
  const finalOgDescription = ogDescription || finalDescription;
  const siteName = brand?.name || '';

  return (
    <>
      <Helmet>
        <title>{finalTitle}</title>
        <meta name="description" content={finalDescription} />
        {finalCanonical && <link rel="canonical" href={finalCanonical} />}
        
        {/* Open Graph */}
        <meta property="og:title" content={finalOgTitle} />
        <meta property="og:description" content={finalOgDescription} />
        <meta property="og:type" content={pageType === 'article' ? 'article' : 'website'} />
        {finalCanonical && <meta property="og:url" content={finalCanonical} />}
        {finalOgImage && <meta property="og:image" content={finalOgImage} />}
        {siteName && <meta property="og:site_name" content={siteName} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={finalOgTitle} />
        <meta name="twitter:description" content={finalOgDescription} />
        {finalOgImage && <meta name="twitter:image" content={finalOgImage} />}
        
        {/* Robots */}
        {noIndex && <meta name="robots" content="noindex, nofollow" />}
        
        {/* Google Site Verification */}
        {siteVerificationId && <meta name="google-site-verification" content={siteVerificationId} />}
      </Helmet>

      {/* Structured Data */}
      <WebSiteJsonLd 
        name={siteName} 
        url={finalCanonical} 
        description={finalDescription} 
      />
      <OrganizationJsonLd template={template} />
      <LocalBusinessJsonLd content={content} />
      {includeFAQ && faq.length > 0 && <FAQJsonLd faqs={faq} />}
    </>
  );
};
